import type { Asset, Concept, ViewId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import dayjs from "dayjs";
import { TbBook, TbHelpCircle, TbStack } from "solid-icons/tb";
import { For, type Setter, Show, createEffect, createSignal, onCleanup } from "solid-js";
import { twMerge } from "tailwind-merge";
import { useWire } from "~/wire";
import type { FilterBuilder } from "../filtering/filterBuilder";
import { TbStackPlus } from "./TbStackPlus";
import type { EntitySelection } from "./useEntitySelection";

export const KnowledgePaneViews = (props: {
  filterBuilder: FilterBuilder<Asset | Concept>;
  assetsSelection: EntitySelection;
  conceptsSelection: EntitySelection;
  hasViewChanges: boolean;

  editView: boolean;
  setEditView: Setter<boolean>;
}) => {
  const wire = useWire();
  const { knowledgeUi } = wire;
  const isActive = (id: "all" | ViewId | "new") => knowledgeUi.activeView() === id;

  const activeViewId = () => knowledgeUi.activeView();

  const activeView = () => {
    const id = activeViewId();
    if (id === "all") {
      return { id: "all", name: "All Knowledge" };
    }
    if (id && id !== "new") {
      const view = wire.views.all().find((v) => v.id === id);
      if (view) return view;
    }
    return null;
  };

  const inactiveViews = () => {
    const views = wire.views.all();
    const id = activeViewId();
    // Filter out the active view
    const filteredViews = views.filter((v) => v.id !== id);

    // If "All Knowledge" is inactive, add it as the first item
    if (id !== "all") {
      return [
        { id: "all", name: "All Knowledge" },
        ...filteredViews.sort((a, b) => dayjs(b.modifiedAt || b.createdAt).diff(dayjs(a.modifiedAt || a.createdAt))),
      ];
    }

    // Otherwise, just return filtered views sorted by date
    return filteredViews.sort((a, b) => dayjs(b.modifiedAt || b.createdAt).diff(dayjs(a.modifiedAt || a.createdAt)));
  };

  const [isScrollable, setIsScrollable] = createSignal(false);
  let scrollContainerRef: HTMLDivElement | undefined;

  // Check if the container is scrollable
  const checkScrollability = () => {
    if (scrollContainerRef) {
      const isScrollableValue = scrollContainerRef.scrollWidth > scrollContainerRef.clientWidth;
      setIsScrollable(isScrollableValue);
    }
  };

  createEffect(() => {
    // Re-check when views change or active view changes
    inactiveViews();
    activeViewId();
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      checkScrollability();
    });
  });

  // Set up resize observer to detect when container size changes
  createEffect(() => {
    if (scrollContainerRef) {
      const resizeObserver = new ResizeObserver(() => {
        checkScrollability();
      });
      resizeObserver.observe(scrollContainerRef);

      // Initial check
      checkScrollability();

      onCleanup(() => {
        resizeObserver.disconnect();
      });
    }
  });

  return (
    <>
      <span class="text-sm text-secondary-text mr-2 shrink-0 font-semibold">Active Collection:</span>

      {/* Active view - fixed, never scrolls */}
      <Show when={activeView()}>
        {(view) => (
          <>
            <StButton
              data-active={true}
              iconClass="size-5"
              onClick={() => knowledgeUi.setActiveView(view().id === "all" ? "all" : view().id)}
              noColor={true}
              icon={view().id === "all" ? TbBook : TbStack}
              class="py-0.5 shrink-0 bg-brand hover:bg-brand-hover text-on-brand border-0"
            >
              {view().name}
            </StButton>
            {/* Visual separator between active and inactive views */}
            <div class="h-6 w-px bg-secondary-text/40 shrink-0 mx-1" aria-hidden="true" />
          </>
        )}
      </Show>

      {/* Scrollable container for inactive views only */}
      <div class="flex-1 min-w-0 relative rounded-md bg-panel-subtle/30 dark:bg-panel-subtle/20 px-1 -mx-1">
        <div ref={scrollContainerRef} class="flex items-center gap-2 flex-nowrap overflow-x-auto scrollbar-hide">
          <For each={inactiveViews()}>
            {(view) => (
              <StButton
                data-active={false}
                iconClass="size-5"
                onClick={() => knowledgeUi.setActiveView(view.id)}
                noColor={false}
                icon={view.id === "all" ? TbBook : TbStack}
                class={twMerge(
                  "py-0.5 shrink-0",
                  view.id === "all" ? "bg-panel dark:border-primary-border" : "dark:border-primary-border",
                )}
              >
                {view.name}
              </StButton>
            )}
          </For>

          {/* New View button inside scrollable container when not scrollable */}
          <Show when={wire.grants.canCreate("cp.views") && !isScrollable()}>
            <StButton
              data-active={isActive("new")}
              icon={TbStackPlus}
              iconClass="size-5"
              noBorder
              onClick={() => {
                knowledgeUi.setActiveView("new");
              }}
              class={twMerge(
                "py-1 dark:border-primary-border shrink-0",
                isActive("new") && "bg-brand hover:bg-brand-hover text-on-brand",
              )}
            >
              New Collection
            </StButton>
            {/* Learn link when no custom views exist */}
            <Show when={wire.views.all().length === 0}>
              <a
                href="https://go.storytell.ai/learn-collections"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center gap-1 underline text-brand text-xs shrink-0"
              >
                <TbHelpCircle />
                Learn about Collections
              </a>
            </Show>
          </Show>
        </div>
        {/* Fade overlay on right when scrollable */}
        <Show when={isScrollable()}>
          <div
            class="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-r from-transparent to-panel pointer-events-none"
            aria-hidden
          />
        </Show>
      </div>

      {/* New View button sticky to right when scrollable */}
      <Show when={wire.grants.canCreate("cp.views") && isScrollable()}>
        <div class="shrink-0 sticky right-0 flex items-center gap-2">
          <div class="w-4 bg-gradient-to-r from-transparent to-panel pointer-events-none h-8" aria-hidden />
          <StButton
            data-active={isActive("new")}
            icon={TbStackPlus}
            iconClass="size-5"
            noBorder
            onClick={() => {
              knowledgeUi.setActiveView("new");
            }}
            class={twMerge(
              "py-1 dark:border-primary-border",
              isActive("new") && "bg-brand hover:bg-brand-hover text-on-brand",
            )}
          >
            New Collection
          </StButton>
          {/* Learn link when no custom views exist */}
          <Show when={wire.views.all().length === 0}>
            <a
              href="https://go.storytell.ai/learn-collections"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 underline text-brand text-xs shrink-0"
            >
              <TbHelpCircle />
              Learn about Collections
            </a>
          </Show>
        </div>
      </Show>
    </>
  );
};
